import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface StockMap {
  [productId: number]: number;
}

/**
 * Fetches live stock for a list of product IDs in a single query.
 * Polls every 60s to stay fresh without hammering the DB.
 */
export function useProductsStock(productIds: number[]) {
  const [stockMap, setStockMap] = useState<StockMap>({});
  const [loading, setLoading] = useState(true);

  const fetchStock = useCallback(async () => {
    if (productIds.length === 0) { setLoading(false); return; }
    try {
      const { data } = await supabase
        .from('products')
        .select('id, stock')
        .in('id', productIds);

      if (data) {
        const map: StockMap = {};
        data.forEach((row) => { map[row.id] = row.stock ?? 0; });
        setStockMap(map);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [productIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    fetchStock();
    const interval = setInterval(fetchStock, 60_000);
    return () => clearInterval(interval);
  }, [fetchStock]);

  /** Get live stock for a product, falls back to undefined while loading */
  const getStock = (id: number): number | undefined =>
    loading ? undefined : stockMap[id];

  return { stockMap, loading, getStock };
}
